import React, { useEffect, useState } from "react";
import "./OfferPopup.css";
import API_URLS from "./apiUrls";

export interface AppliedCoupon {
  code: string;
  discount_amount: number;
  final_amount: number;
}

type Offer = {
  id: string;
  title: string;
  subtitle?: string;
  code?: string;
  validity?: string;
  description?: string;
};

export interface OfferPopupProps {
  visible: boolean;
  onClose: () => void;
  onPayNow: () => void;
  isHotelOffers?: boolean;
  bookingAmount?: number;
  appliedCoupon?: AppliedCoupon | null;
  onCouponApplied?: (coupon: AppliedCoupon | null) => void;
}

const HARDCODED_OFFERS_HOTEL_OFFERS: Offer[] = [
  {
    id: "OFFER1",
    title: "Early Bird - 10% OFF",
    subtitle: "Save on advance bookings",
    code: "EARLY10",
    validity: "Valid till: Dec 31, 2025",
    description: "Apply at checkout for 10% off.",
  },
  {
    id: "OFFER2",
    title: "Stay More Save More - ₹500 OFF",
    subtitle: "On stays 3+ nights",
    code: "STAY500",
    validity: "Valid till: Jan 31, 2026",
    description: "Save ₹500 on selected stays of 3 nights or more.",
  },
  {
    id: "OFFER3",
    title: "Festive Cashback",
    subtitle: "₹250 cashback",
    code: "FEST250",
    validity: "Valid till: Dec 20, 2025",
    description: "Get ₹250 cashback on festive bookings.",
  },
];

const HARDCODED_OFFERS_FLIGHT_OFFERS: Offer[] = [
  {
    id: "OFFER4",
    title: "Flight Saver - 5% OFF",
    subtitle: "Discount on round-trip flights",
    code: "FLY5OFF",
    validity: "Valid till: Nov 30, 2025",
    description: "Get 5% off on round-trip flight bookings.",
  },
  {
    id: "OFFER5",
    title: "Weekend Getaway - ₹300 OFF",
    subtitle: "For weekend flights",
    code: "WEEKEND300",
    validity: "Valid till: Dec 15, 2025",
    description: "Save ₹300 on weekend flight bookings.",
  },
  {
    id: "OFFER6",
    title: "Holiday Special Cashback",
    subtitle: "₹400 cashback",
    code: "HOLIDAY400",
    validity: "Valid till: Jan 10, 2026",
    description: "Receive ₹400 cashback on holiday flight bookings.",
  },
];

const OfferPopup: React.FC<OfferPopupProps> = ({
  visible,
  onClose,
  onPayNow,
  isHotelOffers,
  bookingAmount = 0,
  appliedCoupon,
  onCouponApplied,
}) => {
  const [couponInput, setCouponInput] = useState("");
  const [validating, setValidating] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [liveOffers, setLiveOffers] = useState<Offer[] | null>(null);

  useEffect(() => {
    if (!visible) return;
    fetch(API_URLS.COUPONS.ACTIVE)
      .then((r) => r.json())
      .then((data) => {
        const items: any[] = Array.isArray(data) ? data : (data.coupons || data.data || []);
        if (items.length > 0) {
          setLiveOffers(
            items.map((c: any) => ({
              id: String(c.id || c.code),
              title: c.title || c.name || c.code,
              subtitle: c.subtitle || c.description || "",
              code: c.code,
              validity: c.expiry_date
                ? `Valid till: ${new Date(c.expiry_date).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}`
                : undefined,
              description: c.description || "",
            }))
          );
        }
      })
      .catch(() => {
        /* fall back to hardcoded offers */
      });
  }, [visible]);

  if (!visible) return null;

  const fallbackOffers = isHotelOffers ? HARDCODED_OFFERS_HOTEL_OFFERS : HARDCODED_OFFERS_FLIGHT_OFFERS;
  const displayOffers = liveOffers ?? fallbackOffers;

  const handleApplyCoupon = async (codeOverride?: string) => {
    const code = (codeOverride || couponInput).trim();
    if (!code) return;
    setValidating(true);
    setCouponError(null);
    try {
      const res = await fetch(API_URLS.COUPONS.VALIDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, booking_amount: bookingAmount }),
      });
      const data = await res.json();
      if (data.valid) {
        onCouponApplied?.({
          code: code.toUpperCase(),
          discount_amount: data.discount_amount,
          final_amount: data.final_amount,
        });
        setCouponInput("");
      } else {
        setCouponError(data.message || "Invalid coupon code.");
      }
    } catch {
      setCouponError("Failed to validate coupon. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponApplied?.(null);
    setCouponInput("");
    setCouponError(null);
  };

  return (
    <div className="offer-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="offer-modal-title">
      <div className="offer-modal">
        <button className="offer-close-btn" aria-label="Close offers" onClick={onClose}>
          ×
        </button>

        <div className="offer-header">
          <h3 id="offer-modal-title">Offers & Coupons</h3>
          <p className="offer-sub">Apply a coupon code or browse available offers.</p>
        </div>

        {/* Coupon input section */}
        <div className="offer-coupon-section" style={{ padding: "0 16px 16px" }}>
          {appliedCoupon ? (
            <div
              className="coupon-applied-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ {appliedCoupon.code}</span>
              <span style={{ color: "#15803d", fontSize: 13 }}>−₹{appliedCoupon.discount_amount.toLocaleString()} off</span>
              <button
                onClick={handleRemoveCoupon}
                style={{ marginLeft: "auto", background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 600 }}
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter coupon code"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value.toUpperCase());
                    setCouponError(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                  style={{ flex: 1, fontSize: 14 }}
                />
                <button
                  className="btn btn-primary btn-sm px-3"
                  onClick={() => handleApplyCoupon()}
                  disabled={validating || !couponInput.trim()}
                >
                  {validating ? "..." : "Apply"}
                </button>
              </div>
              {couponError && (
                <p style={{ color: "#dc2626", fontSize: 12, marginTop: 4, marginBottom: 0 }}>{couponError}</p>
              )}
            </>
          )}
        </div>

        <div className="offer-list">
          {displayOffers.map((o) => (
            <div className="offer-card" key={o.id}>
              <div className="offer-card-left">
                <div className="offer-tag">{o.title}</div>
                <div className="offer-code">{o.code}</div>
              </div>
              <div className="offer-card-right">
                <div className="offer-desc">
                  <strong>{o.subtitle}</strong>
                  <div className="small-text">{o.description}</div>
                  <div className="small-text text-muted">{o.validity}</div>
                </div>
                <div className="offer-actions">
                  <button
                    className="btn btn-apply"
                    disabled={validating}
                    onClick={() => {
                      setCouponError(null);
                      handleApplyCoupon(o.code);
                    }}
                  >
                    {validating ? "..." : "Use"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="offer-footer">
          <div className="offer-total-info">
            {appliedCoupon ? (
              <small style={{ color: "#16a34a", fontWeight: 500 }}>
                Coupon applied! You save ₹{appliedCoupon.discount_amount.toLocaleString()}. Total: ₹
                {appliedCoupon.final_amount.toLocaleString()}
              </small>
            ) : (
              <small className="text-muted">Apply a coupon above to get a discount on your booking.</small>
            )}
          </div>
          <div className="offer-footer-actions">
            <button className="btn btn-outline" onClick={onClose}>
              Continue editing
            </button>
            <button
              className="btn btn-paynow"
              onClick={() => {
                onClose();
                setTimeout(() => onPayNow(), 50);
              }}
            >
              Pay Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferPopup;
